import { getPrefix } from './getPrefix';

const prefixPath = getPrefix();
const homepage = `${prefixPath}/` || '/';

const ROUTES = {
  PAGE_HOME: {
    url: homepage,
    analytics: {
      pageID: 'login',
      pageTitle: 'Login',
    },
  },
  ACTIVATION_CODE: {
    url: `${prefixPath}/activation-code`,
    analytics: {
      pageID: 'activation-code',
      pageTitle: 'Activation Code',
    },
  },
  ACTIVATION_SUCCESS: {
    url: `${prefixPath}/activation-success`,
    analytics: {
      pageID: 'activation-success',
      pageTitle: 'Activation Success',
    },
  },
  PAGE_REGISTER: {
    url: `${prefixPath}/register`,
    analytics: {
      pageID: 'register',
      pageTitle: 'Register',
    },
  },
  PAGE_ACTIVATE: {
    url: `${prefixPath}/activate`,
  },
  PAGE_VALIDATE: {
    url: `${prefixPath}/validate`,
    analytics: {
      pageID: 'email',
      pageTitle: 'Email',
    },
  },
  PAGE_LOGIN: {
    url: `${prefixPath}/login`,
    analytics: {
      pageID: 'return',
      pageTitle: 'Welcome Back',
    },
  },
  PAGE_RESET_SUCCESS: {
    url: `${prefixPath}/reset_success`,
    analytics: {
      pageID: 'resetsuccess',
      pageTitle: 'Reset Success',
    },
  },
  PAGE_LOGIN_SUCCESS: {
    url: `${prefixPath}/login_success`,
    analytics: {
      pageID: 'loginsuccess',
      pageTitle: 'Login Success',
    },
  },
  PAGE_LOGOUT: {
    url: `${prefixPath}/logout`,
    analytics: {
      pageID: 'logout',
      pageTitle: 'Logout',
    },
  },
  PAGE_UPDATES_REQUIRED: {
    url: `${prefixPath}/update_required`,
    analytics: {
      pageID: 'updaterequired',
      pageTitle: 'Update Required',
    },
  },
  PAGE_FORGOT_PASSWORD: {
    url: `${prefixPath}/forgot_password`,
    analytics: {
      pageID: 'forgotpassword',
      pageTitle: 'Forgot Password',
    },
  },
  PAGE_NOTIFICATION: {
    url: `${prefixPath}/notification`,
    analytics: {
      pageID: 'notification',
      pageTitle: 'Notification',
    },
  },
  PAGE_TERMS: {
    url: `${prefixPath}/terms`,
    analytics: {
      pageID: 'terms',
      pageTitle: 'Terms',
    },
  },
  PAGE_FORGOT_PASSWORD_CALLBACK: {
    url: `${prefixPath}/new_password`,
    analytics: {
      pageID: 'newpassword',
      pageTitle: 'New Password',
    },
  },
  PAGE_SOCIAL_LOGIN: {
    url: `${prefixPath}/social_login`,
    analytics: {
      pageID: 'sociallogin',
      pageTitle: 'Social Login',
    },
  },
  PAGE_SOCIAL_TOKEN: {
    url: `${prefixPath}/social`,
    analytics: {
      pageID: 'socialtoken',
      pageTitle: 'Social Token',
    },
  },
  PAGE_PROFILE: {
    url: `${prefixPath}/profile`,
    analytics: {
      pageID: 'profile',
      pageTitle: 'Profile',
    },
    nestedRoutes: {
      interests: {
        url: '/interests',
        analytics: {
          pageID: 'interests',
          pageTitle: 'Interests',
        },
      },
      library: {
        url: '/library',
        analytics: {
          pageID: 'library',
          pageTitle: 'Library',
        },
      },
      settings: {
        url: '/settings',
        analytics: {
          pageID: 'settings',
          pageTitle: 'Settings',
        },
      },
      support: {
        url: '/support',
        analytics: {
          pageID: 'support',
          pageTitle: 'Support',
        },
      },
    },
  },
  PAGE_LOGGING_IN: {
    url: `${prefixPath}/logging_in`,
    analytics: {
      pageID: 'loggingin',
      pageTitle: 'Logging In',
    },
  },
  PAGE_SET_INTERESTS: {
    url: `${prefixPath}/set_interests`,
    analytics: {
      pageID: 'interests',
      pageTitle: 'Interests',
    },
  },
  PAGE_SET_PASSWORD: {
    url: `${prefixPath}/set_password`,
    analytics: {
      pageID: 'setpassword',
      pageTitle: 'Set Password',
    },
  },
  WELCOME_BACK: {
    url: `${prefixPath}/welcome_back`,
    analytics: {
      pageID: 'welcome_back',
      pageTitle: 'Welcome Back',
    },
  },
  PAGE_RESEND_VERIFICATION: {
    url: `${prefixPath}/resend_verification`,
    analytics: {
      pageID: 'resend_verification',
      pageTitle: 'Resend Verification',
    },
  },
  PAGE_VERIFICATION: {
    url: `${prefixPath}/verification`,
    analytics: {
      pageID: 'verification',
      pageTitle: 'Verification',
    },
  },
  PAGE_VERIFICATION_CODE: {
    url: `${prefixPath}/verification_code`,
    analytics: {
      pageID: 'verification_code',
      pageTitle: 'Verification Code',
    },
  },
  PAGE_VERIFICATION_SENT: {
    url: `${prefixPath}/verification_sent`,
    analytics: {
      pageID: 'verification_sent',
      pageTitle: 'Verification Email Sent',
    },
  },
};

/**
 * Several page urls don't need to be redirected to the PAGE_LOGIN_SUCCESS or PAGE_PROFILE when session is still valid.
 */
const ENTRY_PAGES = (() => {
  const pageNames = [
    'PAGE_FORGOT_PASSWORD_CALLBACK',
    'PAGE_PROFILE',
    'PAGE_LOGOUT',
    'ACTIVATION_CODE',
    'ACTIVATION_SUCCESS',
    'WELCOME_BACK',
    'PAGE_RESEND_VERIFICATION',
    'PAGE_VERIFICATION_SENT',
  ];
  return pageNames
    .map((name) => {
      const { url, nestedRoutes } = ROUTES[name];
      const routes = [url];
      if (nestedRoutes) {
        routes.push(
          ...Object.values(nestedRoutes).map((nestedRoute) => {
            return `${url}${nestedRoute.url}`;
          })
        );
      }
      return routes;
    })
    .reduce((acc, val) => acc.concat(val), []);
})();

/**
 * Pages that need to include the terms and privacy policy footer
 */
const POLICY_PAGES = [
  ROUTES.PAGE_HOME.url,
  ROUTES.PAGE_SOCIAL_LOGIN.url,
  ROUTES.PAGE_LOGIN.url,
  ROUTES.PAGE_SET_INTERESTS.url,
  ROUTES.PAGE_VALIDATE.url,
  ROUTES.PAGE_VERIFICATION_CODE.url,
];

const PROVIDERS = {
  facebook: {
    darkLoader: false,
    key: 'facebook',
    title: 'Facebook',
  },
  google: {
    darkLoader: true,
    key: 'google',
    title: 'Google',
  },
  'apple-id': {
    darkLoader: true,
    key: 'apple',
    title: 'Apple',
  },
};

export { ROUTES, ENTRY_PAGES, POLICY_PAGES, PROVIDERS };
